/**
 * Screen print batching: same normalized design name + identical F/B/O ink sets
 * => one physical setup (lump lines / share screens). Same design with different
 * inks => treat as separate runs; scheduling adds ink-swap time between them.
 */

export const normalizeDesignNameForBatch = (name) =>
  (name || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const normalizeInkToken = (c) => {
  if (c == null) return '';
  if (typeof c === 'string') return c.trim().toLowerCase();
  if (typeof c === 'object') {
    const s = c.name ?? c.label ?? c.code ?? c.value ?? '';
    return String(s).trim().toLowerCase();
  }
  return String(c).trim().toLowerCase();
};

/** Canonical F/B/O ink signature for equality (order of colors ignored). */
export const getInkFingerprint = (line) => {
  const pack = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return '';
    return [...arr].map(normalizeInkToken).filter(Boolean).sort().join('|');
  };
  const f = pack(line?.frontInkColors);
  const b = pack(line?.backInkColors);
  const o = pack(line?.otherInkColors);
  return `f:${f}::b:${b}::o:${o}`;
};

/**
 * Lines with the same key can share one screen setup / one combined print run.
 * - forceSeparateScreens: always its own batch
 * - screenGroupId: manual batch identity (siblings across orders)
 * - default: normalized design + exact ink fingerprint (cross-order OK)
 */
export const getScreenPrintBatchKey = (line) => {
  if (!line) return '';
  if (line.forceSeparateScreens === true) return `separate:${line.id}`;
  if (line.screenGroupId) {
    const sid = normalizeDesignNameForBatch(String(line.screenGroupId));
    return `custom:${sid}`;
  }
  const d = normalizeDesignNameForBatch(line.designName);
  const ink = getInkFingerprint(line);
  return `${d}::${ink}`;
};

/** ScreenList alias — same grouping semantics */
export const getScreenGroupKey = getScreenPrintBatchKey;
